import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Plus, 
  Edit, 
  Trash2, 
  BookOpen, 
  Leaf, 
  Search, 
  Loader2,
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FileText,
  TrendingUp,
  TrendingDown,
  Wallet,
  PiggyBank,
  CreditCard,
  Sparkles
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCOUNT_TYPES = [
  { value: "Asset", color: "bg-blue-100 text-blue-700", icon: Wallet },
  { value: "Liability", color: "bg-red-100 text-red-700", icon: CreditCard },
  { value: "Equity", color: "bg-purple-100 text-purple-700", icon: PiggyBank },
  { value: "Revenue", color: "bg-emerald-100 text-emerald-700", icon: TrendingUp },
  { value: "Expense", color: "bg-amber-100 text-amber-700", icon: TrendingDown },
];

const CATEGORIES = {
  Asset: ["Current Assets", "Fixed Assets", "Intangible Assets"],
  Liability: ["Current Liabilities", "Long-term Liabilities"],
  Equity: ["Equity", "Retained Earnings"],
  Revenue: ["Operating Revenue", "Other Income"],
  Expense: ["Cost of Goods Sold", "Operating Expenses", "Administrative Expenses"],
};

const EMISSION_SCOPES = ["None", "Scope 1", "Scope 2", "Scope 3"];

export default function ChartOfAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedTypes, setExpandedTypes] = useState(["Asset", "Liability", "Equity", "Revenue", "Expense"]);
  const [expandedCategories, setExpandedCategories] = useState([]);

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    // Auto-expand all categories on load
    const allCategories = [];
    Object.values(CATEGORIES).forEach(cats => {
      cats.forEach(cat => allCategories.push(cat));
    });
    setExpandedCategories(allCategories);
  }, []);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    type: "",
    category: "",
    emission_factor: 0,
    emission_scope: "None",
    description: "",
    is_active: true,
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    // Auto-expand all categories on load
    const allCategories = [];
    Object.values(CATEGORIES).forEach(cats => {
      cats.forEach(cat => allCategories.push(cat));
    });
    setExpandedCategories(allCategories);
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`);
      setAccounts(response.data);
    } catch (error) {
      toast.error("Failed to fetch accounts");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name || !formData.type || !formData.category) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      if (editingAccount) {
        await axios.put(`${API}/accounts/${editingAccount.id}`, formData);
        toast.success("Account updated");
      } else {
        await axios.post(`${API}/accounts`, formData);
        toast.success("Account created");
      }
      setDialogOpen(false);
      resetForm();
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save account");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      code: account.code,
      name: account.name,
      type: account.type,
      category: account.category,
      emission_factor: account.emission_factor,
      emission_scope: account.emission_scope,
      description: account.description || "",
      is_active: account.is_active,
    });
    setDialogOpen(true);
  };

  const handleCleanup = async () => {
    setCleaning(true);
    try {
      const response = await axios.delete(`${API}/accounts/reset`);
      const { deleted_count = 0, kept_in_use = 0 } = response.data || {};
      if (deleted_count === 0) {
        toast.info("Nothing to clean up — all accounts are in use");
      } else {
        toast.success(`Removed ${deleted_count} unused account${deleted_count === 1 ? "" : "s"} · kept ${kept_in_use} in use`);
      }
      setCleanupOpen(false);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clean up accounts");
    } finally {
      setCleaning(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/accounts/${id}`);
      toast.success("Account deleted");
      fetchAccounts();
    } catch (error) {
      toast.error("Failed to delete account");
    }
  };

  const resetForm = () => {
    setEditingAccount(null);
    setFormData({
      code: "",
      name: "",
      type: "",
      category: "",
      emission_factor: 0,
      emission_scope: "None",
      description: "",
      is_active: true,
    });
  };

  const toggleType = (type) => {
    setExpandedTypes(prev => 
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  // Filter accounts by search
  const filteredAccounts = accounts.filter((acc) => {
    if (!searchTerm) return true;
    return (
      acc.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Build tree structure
  const buildTree = () => {
    const tree = {};
    
    ACCOUNT_TYPES.forEach(type => {
      tree[type.value] = {
        ...type,
        categories: {},
        totalAccounts: 0
      };
      
      CATEGORIES[type.value]?.forEach(category => {
        tree[type.value].categories[category] = {
          accounts: [],
          totalAccounts: 0
        };
      });
    });

    filteredAccounts.forEach(account => {
      if (tree[account.type] && tree[account.type].categories[account.category]) {
        tree[account.type].categories[account.category].accounts.push(account);
        tree[account.type].categories[account.category].totalAccounts++;
        tree[account.type].totalAccounts++;
      }
    });

    return tree;
  };

  const tree = buildTree();

  const getTypeColor = (type) => {
    const typeInfo = ACCOUNT_TYPES.find((t) => t.value === type);
    return typeInfo ? typeInfo.color : "bg-gray-100 text-gray-700";
  };

  const getScopeColor = (scope) => {
    switch (scope) {
      case "Scope 1": return "bg-amber-100 text-amber-700";
      case "Scope 2": return "bg-blue-100 text-blue-700";
      case "Scope 3": return "bg-emerald-100 text-emerald-700";
      default: return "";
    }
  };

  // Summary counts
  const accountsByType = accounts.reduce((acc, account) => {
    acc[account.type] = (acc[account.type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="chart-of-accounts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#1a2e22] font-['Outfit'] tracking-tight">
            Chart of Accounts
          </h1>
          <p className="text-[#5c6b63] mt-2">
            Manage your sustainability-linked accounts
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AlertDialog open={cleanupOpen} onOpenChange={setCleanupOpen}>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="flex items-center gap-2"
                data-testid="cleanup-accounts-btn"
              >
                <Sparkles className="w-4 h-4" />
                Clean up unused
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="font-['Outfit']">
                  Clean up unused accounts?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently remove every account in your Chart of Accounts that is{" "}
                  <span className="font-semibold text-[#1a2e22]">not referenced by any transaction</span>.
                  Accounts that are in use will be kept untouched. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={cleaning} data-testid="cleanup-cancel-btn">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    handleCleanup();
                  }}
                  disabled={cleaning}
                  data-testid="cleanup-confirm-btn"
                  className="bg-[#1a2e22] hover:bg-[#2d4a3e]"
                >
                  {cleaning ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Yes, clean up"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button className="btn-primary flex items-center gap-2" data-testid="add-account-btn">
                <Plus className="w-4 h-4" />
                Add Account
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-lg z-50">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">
                {editingAccount ? "Edit Account" : "Add New Account"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Account Code *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="1000"
                    className="h-11 bg-[#f2f7f4]/50 border-transparent focus:border-[#1a2e22]/20 focus:bg-white font-mono"
                    data-testid="account-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Type *</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) =>
                      setFormData({ ...formData, type: value, category: "" })
                    }
                  >
                    <SelectTrigger className="h-11" data-testid="account-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Account Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Office Equipment"
                  className="h-11 bg-[#f2f7f4]/50 border-transparent focus:border-[#1a2e22]/20 focus:bg-white"
                  data-testid="account-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                  disabled={!formData.type}
                >
                  <SelectTrigger className="h-11" data-testid="account-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.type &&
                      CATEGORIES[formData.type]?.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-4 bg-emerald-50/50 rounded-xl space-y-4">
                <div className="flex items-center gap-2 text-emerald-700">
                  <Leaf className="w-4 h-4" />
                  <span className="font-medium">Carbon Tracking</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Emission Factor (kg CO₂e/£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.emission_factor}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          emission_factor: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="h-11 bg-white border-emerald-200 font-mono"
                      data-testid="emission-factor-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Emission Scope</Label>
                    <Select
                      value={formData.emission_scope}
                      onValueChange={(value) =>
                        setFormData({ ...formData, emission_scope: value })
                      }
                    >
                      <SelectTrigger className="h-11" data-testid="emission-scope-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EMISSION_SCOPES.map((scope) => (
                          <SelectItem key={scope} value={scope}>
                            {scope}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description..."
                  className="bg-[#f2f7f4]/50 border-transparent focus:border-[#1a2e22]/20 focus:bg-white min-h-[80px]"
                  data-testid="account-description-input"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-[#f2f7f4] rounded-xl">
                <Label htmlFor="is-active" className="cursor-pointer">
                  Account Active
                </Label>
                <Switch
                  id="is-active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                  data-testid="account-active-switch"
                />
              </div>

              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full btn-accent h-11"
                data-testid="save-account-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : editingAccount ? (
                  "Update Account"
                ) : (
                  "Create Account"
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {ACCOUNT_TYPES.map((type) => {
          const TypeIcon = type.icon;
          return (
            <Card key={type.value} className="kpi-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#5c6b63]">{type.value}</p>
                    <p className="text-2xl font-bold text-[#1a2e22] font-['Outfit']">
                      {accountsByType[type.value] || 0}
                    </p>
                  </div>
                  <div className={`w-10 h-10 rounded-xl ${type.color} flex items-center justify-center`}>
                    <TypeIcon className="w-5 h-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5c6b63] z-10" />
            <Input
              placeholder="Search by code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11 bg-[#f2f7f4]/50 border-transparent focus:border-[#1a2e22]/20 focus:bg-white"
              data-testid="search-accounts"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tree View */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-semibold text-[#1a2e22] font-['Outfit']">
            <BookOpen className="w-5 h-5" />
            Account Structure ({filteredAccounts.length} accounts)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#1a2e22] animate-spin" />
            </div>
          ) : filteredAccounts.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-[#c8d4cc] mx-auto mb-4" />
              <p className="text-[#5c6b63] mb-2">No accounts found</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="accounts-tree">
              {ACCOUNT_TYPES.map((typeInfo) => {
                const typeData = tree[typeInfo.value];
                const isTypeExpanded = expandedTypes.includes(typeInfo.value);
                const TypeIcon = typeInfo.icon;

                if (typeData.totalAccounts === 0 && searchTerm) return null;

                return (
                  <div key={typeInfo.value} className="border border-[#e0e7e3] rounded-xl overflow-hidden">
                    {/* Type Level */}
                    <Collapsible open={isTypeExpanded} onOpenChange={() => toggleType(typeInfo.value)}>
                      <CollapsibleTrigger className="w-full">
                        <div className={`flex items-center justify-between p-4 hover:bg-[#f2f7f4] transition-colors ${isTypeExpanded ? 'bg-[#f2f7f4]' : ''}`}>
                          <div className="flex items-center gap-3">
                            {isTypeExpanded ? (
                              <ChevronDown className="w-5 h-5 text-[#5c6b63]" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-[#5c6b63]" />
                            )}
                            <div className={`w-8 h-8 rounded-lg ${typeInfo.color} flex items-center justify-center`}>
                              <TypeIcon className="w-4 h-4" />
                            </div>
                            <span className="font-semibold text-[#1a2e22]">{typeInfo.value}</span>
                            <Badge variant="secondary" className="ml-2">
                              {typeData.totalAccounts}
                            </Badge>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="border-t border-[#e0e7e3]">
                          {Object.entries(typeData.categories).map(([categoryName, categoryData]) => {
                            const isCategoryExpanded = expandedCategories.includes(categoryName);
                            
                            if (categoryData.accounts.length === 0 && searchTerm) return null;

                            return (
                              <div key={categoryName} className="border-b border-[#e0e7e3] last:border-b-0">
                                {/* Category Level */}
                                <Collapsible open={isCategoryExpanded} onOpenChange={() => toggleCategory(categoryName)}>
                                  <CollapsibleTrigger className="w-full">
                                    <div className={`flex items-center justify-between py-3 px-4 pl-12 hover:bg-[#f8faf9] transition-colors ${isCategoryExpanded ? 'bg-[#f8faf9]' : ''}`}>
                                      <div className="flex items-center gap-3">
                                        {isCategoryExpanded ? (
                                          <ChevronDown className="w-4 h-4 text-[#5c6b63]" />
                                        ) : (
                                          <ChevronRight className="w-4 h-4 text-[#5c6b63]" />
                                        )}
                                        {isCategoryExpanded ? (
                                          <FolderOpen className="w-5 h-5 text-[#5c6b63]" />
                                        ) : (
                                          <Folder className="w-5 h-5 text-[#5c6b63]" />
                                        )}
                                        <span className="text-[#1a2e22] font-medium">{categoryName}</span>
                                        <span className="text-sm text-[#5c6b63]">
                                          ({categoryData.accounts.length})
                                        </span>
                                      </div>
                                    </div>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    {/* Account Items */}
                                    <div className="bg-white">
                                      {categoryData.accounts
                                        .sort((a, b) => a.code.localeCompare(b.code))
                                        .map((account) => (
                                          <div
                                            key={account.id}
                                            className="flex items-center justify-between py-3 px-4 pl-20 hover:bg-[#f2f7f4] transition-colors group border-t border-[#e0e7e3]/50"
                                            data-testid={`account-${account.id}`}
                                          >
                                            <div className="flex items-center gap-3 flex-1">
                                              <FileText className="w-4 h-4 text-[#c8d4cc]" />
                                              <span className="font-mono text-sm text-[#5c6b63] w-16">
                                                {account.code}
                                              </span>
                                              <span className="text-[#1a2e22]">{account.name}</span>
                                              {!account.is_active && (
                                                <Badge variant="outline" className="text-gray-400 border-gray-300">
                                                  Inactive
                                                </Badge>
                                              )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                              {account.emission_factor > 0 && (
                                                <div className="flex items-center gap-2">
                                                  <Leaf className="w-3 h-3 text-emerald-500" />
                                                  <span className="text-xs font-mono text-[#5c6b63]">
                                                    {account.emission_factor.toFixed(2)}
                                                  </span>
                                                  {account.emission_scope !== "None" && (
                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${getScopeColor(account.emission_scope)}`}>
                                                      {account.emission_scope}
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-7 w-7 text-[#5c6b63] hover:text-[#1a2e22]"
                                                  onClick={(e) => { e.stopPropagation(); handleEdit(account); }}
                                                >
                                                  <Edit className="w-3.5 h-3.5" />
                                                </Button>
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-7 w-7 text-[#5c6b63] hover:text-red-600"
                                                  onClick={(e) => handleDelete(account.id, e)}
                                                >
                                                  <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      {categoryData.accounts.length === 0 && (
                                        <div className="py-4 pl-20 text-sm text-[#5c6b63] italic">
                                          No accounts in this category
                                        </div>
                                      )}
                                    </div>
                                  </CollapsibleContent>
                                </Collapsible>
                              </div>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
